import { Spinner } from "common/components/Spinner";
import { SubText } from "common/components/Typography";
import { useRandomId } from "common/hooks/useRandomId";
import { useUploadURL } from "common/hooks/useUploadURL";
import { getFileUpload, getImageUpload } from "common/s3";
import { REQUIRED_FORM_FIELD } from "domain/constants";
import EasyMDE, { Options } from "easymde";
import React, { useState } from "react";
import {
  Controller,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import { FieldError } from "./FieldError";
import { Label } from "./Label";
import { MutationLoadingOrError } from "./MutationLoadingOrError";

const MarkdownEditor = React.lazy(() =>
  import(/* webpackChunkName: "MarkdownEditor" */ "./MarkdownEditor").then(
    (it) => ({
      default: it.MarkdownEditor,
    }),
  ),
);

interface Props<T extends FieldValues> {
  name: Path<T>;
  title: string;
  defaultValue?: FieldPathValue<T, Path<T>>;
  maxWidth?: string;
  required?: boolean;
}

export const MarkdownField = <T extends FieldValues>({
  title,
  name,
  defaultValue,
  maxWidth,
  required,
}: Props<T>): React.ReactElement => {
  const [awsImageUrls, setAwsImageUrls] = useState<string[]>([]);
  const [awsFileUrls, setAwsFileUrls] = useState<string[]>([]);

  const { control } = useFormContext<T>();
  const id = useRandomId();
  const { getUploadURL, error, loading } = useUploadURL();
  const [editorOptions] = useState({
    spellChecker: false,
    toolbar: [
      "bold",
      "italic",
      "heading",
      "quote",
      {
        name: "linebreak",
        action: function linebreak(editor: EasyMDE) {
          const cursor = editor.codemirror.getCursor();
          editor.codemirror.replaceRange(`\n&nbsp;\n`, cursor);
        },
        className: "fa fa-minus",
        title: "Linebreak",
      },
      "unordered-list",
      "ordered-list",
      "table",
      "link",
      {
        name: "image",
        action: function customImage(editor: EasyMDE) {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.onchange = async function () {
            const file = input.files?.[0];
            if (file) {
              const getURL = await getUploadURL("image");
              const imageUrl = await getImageUpload(file, getURL);
              setAwsImageUrls([...awsImageUrls, imageUrl]);
              const cursor = editor.codemirror.getCursor();
              editor.codemirror.replaceRange(
                `\n![${file.name}](${imageUrl})\n`,
                cursor,
              );
            }
          };
          input.click();
        },
        className: "fa fa-image",
        title: "Upload Image",
      },
      {
        name: "file",
        action: function customFile(editor: EasyMDE) {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "*/*"; // Allow all file types
          input.onchange = async function () {
            const file = input.files?.[0];
            if (file) {
              const getURL = await getUploadURL("file");
              const fileUrl = await getFileUpload(file, getURL);
              setAwsFileUrls([...awsFileUrls, fileUrl]);
              editor.codemirror.replaceSelection(
                `\n[${file.name}](${fileUrl})`,
              );
            }
          };
          input.click();
        },
        className: "fa fa-file",
        title: "Upload File",
      },
      "preview",
      "side-by-side",
    ],
    sideBySideFullscreen: false,
  });

  return (
    <div style={{ maxWidth: maxWidth }}>
      <Label htmlFor={id} title={title} />
      <SubText>
        <b>Ikke</b> skriv inn personopplysninger
      </SubText>
      <MutationLoadingOrError loading={loading} error={error} />
      <Controller<T>
        render={({ field }) => (
          <React.Suspense fallback={<Spinner />}>
            <MarkdownEditor
              content={field.value as string | undefined}
              onChange={field.onChange}
              options={editorOptions as Options}
              urls={{
                awsImageUrls,
                setAwsImageUrls,
                awsFileUrls,
                setAwsFileUrls,
              }}
            />
          </React.Suspense>
        )}
        name={name}
        defaultValue={defaultValue}
        control={control}
        rules={
          required
            ? {
                required: REQUIRED_FORM_FIELD,
              }
            : undefined
        }
      />
      <FieldError name={name} />
    </div>
  );
};
